import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Y from 'yup'
import PropTypes from 'prop-types'
import axios from 'axios'
import { default as dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { createYupSchema } from '../../lib/createYUPSchema'
import { InputField } from './formElements/InputField'
import { InputSwitch } from './formElements/InputSwitch'
import { FormButtons } from './FormButtons'
import { useCreateCall } from '../../lib/api/create'
import { DeleteConfirm } from './DeleteConfirm'
import { useUpdateCall } from '../../lib/api/update'
import { InputSelect } from './formElements/InputSelect'
import { TextArea } from './formElements/TextArea'
import { InputAutocomplete } from './formElements/InputAutocomplete'
import { InputFile } from './formElements/InputFile'
import { useDeleteCall } from '../../lib/api/delete'

dayjs.extend(utc)
/**
 * Form component handles rendering of a dynamic form, including input fields, validation, and submission.
 *
 * @module form/Form
 * @description Form component handles rendering of a dynamic form, including input fields, validation, and submission.
 * @param {boolean} [isViewOnly=false] - If true, form fields are read-only.
 * @param {Object} defaultData - Default data for the form fields.
 * @param {Object} formSchema - Schema object defining form fields and validation.
 * @param {Object} moduleSchema - Schema object defining module-specific details.
 * @param {boolean} [isAdd=false] - If true, form is in 'add' mode; otherwise, it's in 'edit' mode.
 * @param {boolean} [isRowForm=false] - If true, form fields are displayed in a row layout.
 * @param {function} [setOpenFormModal] - Function to handle form modal visibility.
 * @param {boolean} isUpdate - If true, form is in update mode.
 * @param {boolean} isProfile - If true, form is handling a user profile.
 * @param {boolean} [addUserObject=false] - If true, adds the user object to the payload.
 * @param {Object} [authDetails] - Object containing authentication details.
 * @param {string} [isLocalEnvironment] - Base URL for the local environment API.
 *
 * @example
 * <Form
 *   isViewOnly={isViewOnly}
 *   defaultData={defaultData}
 *   formSchema={formSchema}
 *   moduleSchema={moduleSchema}
 *   isAdd={isAdd}
 *   isRowForm={isRowForm}
 *   setOpenFormModal={setOpenFormModal}
 *   isUpdate={isUpdate}
 *   isProfile={isProfile}
 *   addUserObject={addUserObject}
 *   authDetails={authDetails}
 *   isLocalEnvironment={isLocalEnvironment}
 * />
 *
 * @returns {React.Element} The rendered Form component.
 */
export const Form = ({
  isViewOnly = false,
  defaultData,
  isChildForm,
  closeChildForm,
  formSchema,
  moduleSchema,
  isAdd = false,
  noClose,
  parentId,
  isRowForm = false,
  isModalForm,
  setOpenFormModal,
  isUpdate,
  isProfile,
  addUserObject = false,
  authDetails,
  isLocalEnvironment,
  prefixValue,
  parentData,
  mainData,
  crud,
}) => {
  const validation = Y.object().shape({
    ...formSchema.buildValidation.reduce(createYupSchema, {}),
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: isAdd ? 'onSubmit' : 'onChange',
    resolver: yupResolver(validation),
  })

  const createCallMutation = useCreateCall()
  const updateCallMutation = useUpdateCall()
  const deleteCallMutation = useDeleteCall()

  const [deleteId, setDeleteId] = React.useState(null)
  const [deleteConfirm, setDeleteConfirm] = React.useState(false)
  const [imageFile, setImageFile] = React.useState(null)

  const confirmDelete = (id) => {
    setDeleteId(id)
    setDeleteConfirm(true)
  }

  const closeDelete = () => {
    setDeleteConfirm(false)
    setDeleteId(null)
  }

  const onCreate = async (data, e) => {
    if (formSchema.hasFile) {
      let formData = new FormData()
      formData.append('docType', data.docType)
      formData.append('file', data.file[0])
      formData['userId'] = authDetails.session.user.userId
      if (isRowForm || isModalForm) {
        await createCallMutation.mutateAsync({
          url: `${isLocalEnvironment}/upload?endPoint=applicationDocType`,
          apiEndpoint: formSchema.formAPI,
          parentAPI: formSchema.parentAPI,
          data: formData, //data,
          headers: {
            tokenSource: 'passport',
            Authorization: `Bearer ${authDetails.authentication.tokenObject}`,
          },
          messageTitle: formSchema.message.title,
          message: formSchema.message.message,
          isMultipart: true,
          isDynamic: true,
          params: '',
        })
      } else {
        await createCallMutation.mutateAsync({
          url: `${isLocalEnvironment}/upload?endPoint=applicationDocType`,
          apiEndpoint: formSchema.formAPI,
          data: formData, //data,
          headers: {
            tokenSource: 'passport',
            Authorization: `Bearer ${authDetails.authentication.tokenObject}`,
          },
          messageTitle: formSchema.message.title,
          message: formSchema.message.message,
          isMultipart: true,
          isDynamic: true,
          params: '',
        })
      }
    } else {
      const payload = { ...data }
      if (formSchema.addParentId) {
        payload[formSchema.parentId] = parentId
      }
      if (formSchema.addUserId) {
        payload[formSchema.userId] = authDetails.session.user.userId
      }

      if (isRowForm || isModalForm) {
        await createCallMutation
          .mutateAsync({
            url: isLocalEnvironment + '/addData',
            apiEndpoint: formSchema.formAPI,
            parentAPI: formSchema.parentAPI,
            data: payload, //data,
            messageTitle: formSchema.message.title,
            message: formSchema.message.message,
            isDynamic: true,
            params: '',
          })
          .then(() => {
            if (isChildForm) {
              closeChildForm(false)
            }
            setOpenFormModal(false)
          })
      } else {
        await createCallMutation
          .mutateAsync({
            url: isLocalEnvironment + '/addData',
            apiEndpoint: formSchema.formAPI,
            parentAPI: formSchema.parentAPI,
            data: payload, //data,
            messageTitle: formSchema.message.title,
            message: formSchema.message.message,
            isDynamic: true,
            params: '',
          })
          .then(() => {
            if (isChildForm && !formSchema.noCloseAfterAdd) {
              closeChildForm(false)
            }
            setOpenFormModal(false)
          })
      }
      if (formSchema.refreshAppData) {
        axios.get(isLocalEnvironment + '/updateAppData' + '&utcOffset=' + dayjs().utcOffset())
      }
      e.target.reset()
    }
  }

  const onUpdate = async (data) => {
    // Merge default data with form data
    for (let prop in defaultData) {
      if (defaultData[prop] && !(prop in data)) {
        data[prop] = defaultData[prop]
      }
    }
    const payload = { ...data }
    if (isProfile) {
      if (payload.password_hash === '' || payload.password_hash === null) {
        await updateCallMutation.mutateAsync({
          url: isLocalEnvironment + '/updateData',
          apiEndpoint: 'userFamily',
          data: payload,
          id: formSchema.isDeleteIdObject
            ? payload[formSchema.deleteIdObject[0]][formSchema.deleteIdObject[1]]
            : payload[formSchema.id],
          messageTitle: formSchema.message.title,
          message: formSchema.message.message,
          params: moduleSchema?.updateLocal
            ? `&endPoint=appuser&id=${
                formSchema.isDeleteIdObject
                  ? payload[formSchema.deleteIdObject[0]][formSchema.deleteIdObject[1]]
                  : payload[formSchema.id]
              }`
            : '',
          isDynamic: true,
        })
      } else {
        const profileData = { ...authDetails?.session?.user, password_hash: payload.password_hash }
        await updateCallMutation.mutateAsync({
          url: isLocalEnvironment + '/userProfile',
          apiEndpoint: '',
          data: profileData,
          id: profileData.userId,
          messageTitle: formSchema.message.title,
          message: formSchema.message.message,
          params: '',
        })
      }
    } else {
      // Handle boolean fields
      if (formSchema.makebool) {
        formSchema.makeboolKey.forEach((k) => (payload[k.k] = k.v))
      }

      // Handle date fields
      if (formSchema.handleDatefields) {
        formSchema.dateFields.forEach((k) => {
          if (payload[k] === 'Invalid Date') {
            payload[k] = null
          }
        })
      }

      if (addUserObject) {
        const plData = authDetails?.session?.user
        plData['password_hash'] = payload['password_hash']

        // Remove unnecessary fields
        ;['UserFamily', 'rowFormData', 'userLogins', 'userRoles', 'userApplications'].forEach(
          (field) => delete plData[field],
        )

        // Handle fingerprint capture
        if (formSchema.captureFingerPrint) {
          plData[formSchema.fingerPrintField] = sessionStorage.getItem('mfsFP')
        }

        const isUpdateEndpoint =
          moduleSchema?.updateLocal === true
            ? { url: isLocalEnvironment, endPoint: moduleSchema.updateLocalEndpoint }
            : { url: isLocalEnvironment, endPoint: formSchema.formAPI }

        await updateCallMutation.mutateAsync({
          url: isLocalEnvironment + '/updateData',
          apiEndpoint: isProfile ? 'userProfile' : isUpdateEndpoint.endPoint,
          data: plData,
          id: plData.userId,
          messageTitle: formSchema.message.title,
          message: formSchema.message.message,
          params: moduleSchema?.updateLocal ? `&endPoint=appuser&id=${plData.userId}` : '',
          isDynamic: true,
        })

        if (formSchema.refreshAppData) {
          axios.get(`${isLocalEnvironment}/updateAppData?utcOffset=${dayjs().utcOffset()}`)
        }
      } else {
        if (formSchema.captureFingerPrint) {
          payload[formSchema.fingerPrintField] = sessionStorage.getItem('mfsFP')
          sessionStorage.removeItem('mfsFP')
        }

        const isUpdateEndpoint =
          moduleSchema?.updateLocal === true
            ? { url: isLocalEnvironment, endPoint: moduleSchema.updateLocalEndpoint }
            : { url: isLocalEnvironment, endPoint: formSchema.formAPI }

        await updateCallMutation
          .mutateAsync({
            url: isLocalEnvironment + '/updateData',
            apiEndpoint: isProfile ? 'userProfile' : isUpdateEndpoint.endPoint,
            parentAPI: formSchema.parentAPI,
            data: payload,
            id: formSchema.isDeleteIdObject
              ? payload[formSchema.deleteIdObject[0]][formSchema.deleteIdObject[1]]
              : payload[formSchema.id],
            messageTitle: formSchema.message.title,
            message: formSchema.message.message,
            params: moduleSchema?.updateLocal
              ? `&endPoint=appuser&id=${
                  formSchema.isDeleteIdObject
                    ? payload[formSchema.deleteIdObject[0]][formSchema.deleteIdObject[1]]
                    : payload[formSchema.id]
                }`
              : '',
            isDynamic: true,
          })
          .then(() => setOpenFormModal(false))

        if (formSchema.refreshAppData) {
          axios.get(`${isLocalEnvironment}/updateAppData?utcOffset=${dayjs().utcOffset()}`)
        }
      }
    }
  }

  const onDelete = async () => {
    if (isRowForm) {
      await deleteCallMutation.mutateAsync({
        url: isLocalEnvironment + '/deleteData',
        apiEndpoint: formSchema.formAPI,
        parentAPI: formSchema.parentAPI,
        id: deleteId,
        messageTitle: formSchema.deleteMessage.title,
        message: formSchema.deleteMessage.message,
        isDynamic: true,
      })
    } else {
      await deleteCallMutation.mutateAsync({
        url: isLocalEnvironment + '/deleteData',
        apiEndpoint: formSchema.formAPI,
        id: deleteId,
        messageTitle: formSchema.deleteMessage.title,
        message: formSchema.deleteMessage.message,
        isDynamic: true,
      })
    }
    if (formSchema.refreshAppData) {
      axios.get(isLocalEnvironment + '/updateAppData?utcOffset=${dayjs().utcOffset()}')
    }
    closeDelete()
  }
  return (
    <form
      key={`${isAdd}${formSchema}`}
      onSubmit={handleSubmit(isAdd ? onCreate : onUpdate)}
      data-testid="formAdd"
      style={{ pointerEvents: isViewOnly ? 'none' : '' }}
    >
      <div className="trtui-row trtui-g-2">
        {formSchema.schema.length > 0 &&
          formSchema.schema.map((field, fieldIndex) =>
            field.element === 'InputField' ? (
              <InputField
                data-testid="inputFieldForm"
                key={`${fieldIndex}${field}${isAdd}`}
                field={field}
                errors={errors}
                register={register}
                isAdd={isAdd}
                defaultData={defaultData}
              />
            ) : field.element === 'SwitchBox' ? (
              <InputSwitch
                key={`${fieldIndex}${field}${isAdd}`}
                field={field}
                errors={errors}
                register={register}
                isAdd={isAdd}
                defaultData={defaultData}
                prefixValue={prefixValue}
              />
            ) : field.element === 'SelectBox' ? (
              <InputSelect
                key={fieldIndex}
                field={field}
                errors={errors}
                register={register}
                isAdd={isAdd}
                defaultData={defaultData}
                setImageFile={setImageFile}
                mainData={mainData}
                control={control}
                parentData={parentData}
                authDetails={authDetails}
              />
            ) : field.element === 'TextArea' ? (
              <TextArea
                key={fieldIndex}
                field={field}
                errors={errors}
                register={register}
                isAdd={isAdd}
                defaultData={defaultData}
                setImageFile={setImageFile}
                mainData={mainData}
              />
            ) : field.element === 'InputAutocomplete' ? (
              <InputAutocomplete
                key={fieldIndex}
                field={field}
                errors={errors}
                register={register}
                isAdd={isAdd}
                defaultData={defaultData}
                setImageFile={setImageFile}
                mainData={mainData}
                suggestions={
                  field.optionAPIDependency &&
                  JSON.parse(sessionStorage.getItem('authent')).state.dependeciesFormAPIS[
                    field.compOptionAPIDependency
                  ]?.formData
                }
              />
            ) : field.element === 'FileChooser' ? (
              <InputFile
                key={`${fieldIndex}${field}${isAdd}`}
                field={field}
                errors={errors}
                register={register}
                isAdd={isAdd}
                defaultData={defaultData}
                prefixValue={prefixValue}
                imageFile={imageFile}
              />
            ) : null,
          )}
        <FormButtons
          isViewOnly={isViewOnly}
          isAdd={isAdd}
          isRowForm={isRowForm}
          isUpdate={isUpdate}
          createCallMutation={createCallMutation}
          updateCallMutation={updateCallMutation}
          deleteCallMutation={deleteCallMutation}
          defaultData={defaultData}
          setOpenFormModal={setOpenFormModal}
          formSchema={formSchema}
          confirmDelete={confirmDelete}
          crud={crud}
          noClose={noClose}
        />
        {deleteConfirm && (
          <DeleteConfirm
            onDelete={onDelete}
            setDeleteId={setDeleteId}
            setDeleteConfirm={setDeleteConfirm}
            deleteCallMutation={{}}
          />
        )}
      </div>
    </form>
  )
}

Form.propTypes = {
  /**
   * If true, form fields are read-only.
   */
  isViewOnly: PropTypes.bool,

  /**
   * Default data for the form fields.
   */
  defaultData: PropTypes.object,
  isChildForm: PropTypes.bool,
  closeChildForm: PropTypes.func,

  /**
   * Schema object defining form fields and validation.
   */
  formSchema: PropTypes.object,

  /**
   * Schema object defining module-specific details.
   */
  moduleSchema: PropTypes.object,

  /**
   * If true, form is in 'add' mode; otherwise, it's in 'edit' mode.
   */
  isAdd: PropTypes.bool,

  /**
   * If true, form fields are displayed in a row layout.
   */
  isRowForm: PropTypes.bool,

  /**
   * Function to handle form modal visibility.
   */
  setOpenFormModal: PropTypes.func,

  /**
   * If true, form is in update mode.
   */
  isUpdate: PropTypes.bool,

  /**
   * If true, form is handling a user profile.
   */
  isProfile: PropTypes.bool,

  isModalForm: PropTypes.bool,
  parentId: PropTypes.string,

  prefixValue: PropTypes.string,
  parentData: PropTypes.object,
  mainData: PropTypes.object,

  /**
   * If true, adds the user object to the payload.
   */
  addUserObject: PropTypes.bool,

  /**
   * Object containing authentication details.
   */
  authDetails: PropTypes.object,

  /**
   * Base URL for the local environment API.
   */
  isLocalEnvironment: PropTypes.string,
  crud: PropTypes.number,
  noClose: PropTypes.bool,
}
